import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { IStepConnectorProps } from '@sixui/ui';
import type { StringUnitLength } from 'luxon';
import {
  faCircleCheck,
  faCircleDashed,
  faTimer,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Step, StepConnector, Stepper } from '@sixui/ui';
import { stepTokens } from '@sixui/ui/components/Step/Step.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';

const styles = stylex.create({
  host: {
    flexGrow: 0,
  },
  step: {
    [stepTokens.bulletPointColor]: colorSchemeTokens.onSurface,
  },
});

export type IPublicationStatusProps = IContainerProps & {
  publicationDate?: DateTime;
  unpublicationDate?: DateTime;
  published?: boolean;
  programmedIcon?: React.ReactNode;
  toggledProgrammedIcon?: React.ReactNode;
  publishedIcon?: React.ReactNode;
  toggledPublishedIcon?: React.ReactNode;
  unpublishedIcon?: React.ReactNode;
  toggledUnpublishedIcon?: React.ReactNode;
};
const relativeDateStyle: StringUnitLength = 'narrow';

type IStatus = 'programmed' | 'draft' | 'published';

const connectorStyles = stylex.create({
  text: {
    whiteSpace: 'nowrap',
  },
});

const Connector: React.FC<IStepConnectorProps> = (props) => (
  <StepConnector styles={connectorStyles} textPosition="top" {...props} />
);

export const PublicationStatus: React.FC<IPublicationStatusProps> = (props) => {
  const {
    sx,
    published: publishedProp,
    publicationDate,
    programmedIcon,
    toggledProgrammedIcon,
    publishedIcon,
    toggledPublishedIcon,
    unpublicationDate,
    toggledUnpublishedIcon,
    unpublishedIcon,
  } = props;
  const now = DateTime.now();
  const { i18n } = useTranslation();
  const locale = i18n.language;

  const faIconByStatus: Record<IStatus, IconProp> = {
    programmed: faTimer,
    draft: faCircleDashed,
    published: faCircleCheck,
  };

  const renderStatusIcon = (
    status: IStatus,
    toggled: boolean,
  ): React.ReactNode => {
    switch (status) {
      case 'programmed':
        return (
          (toggled ? toggledProgrammedIcon : programmedIcon) ??
          programmedIcon ?? <FontAwesomeIcon icon={faIconByStatus.draft} />
        );

      case 'draft':
        return (
          (toggled ? toggledUnpublishedIcon : unpublishedIcon) ??
          unpublishedIcon ?? <FontAwesomeIcon icon={faIconByStatus.draft} />
        );

      case 'published':
        return (
          (toggled ? toggledPublishedIcon : publishedIcon) ??
          publishedIcon ?? <FontAwesomeIcon icon={faIconByStatus.published} />
        );
    }
  };

  const publicationScheduled = !!publicationDate && publicationDate > now;
  const publicationScheduledIn =
    publicationScheduled &&
    publicationDate.toRelative({
      locale,
      style: relativeDateStyle,
    });

  const unpublicationScheduled = !!unpublicationDate && unpublicationDate > now;
  const unpublicationScheduledIn =
    unpublicationScheduled &&
    unpublicationDate.toRelative({
      locale,
      style: relativeDateStyle,
    });
  const unpublicationFromPublicationScheduledIn =
    publicationScheduled &&
    unpublicationScheduled &&
    unpublicationDate.toRelative({
      locale,
      base: publicationDate,
      style: relativeDateStyle,
    });

  const unpublished = !!unpublicationDate && unpublicationDate <= now;
  const published =
    publishedProp &&
    !unpublished &&
    !!publicationDate &&
    publicationDate <= now;

  return (
    <Stepper activeStep={0} sx={[styles.host, sx]}>
      {publicationScheduled || unpublicationScheduled ? (
        publicationScheduled && unpublicationScheduled ? (
          [
            <Step
              key={0}
              sx={styles.step}
              icon={renderStatusIcon('programmed', true)}
              nextConnector={<Connector>{publicationScheduledIn}</Connector>}
            />,
            <Step
              key={1}
              sx={styles.step}
              icon={renderStatusIcon('published', false)}
              nextConnector={
                <Connector>{unpublicationFromPublicationScheduledIn}</Connector>
              }
            />,
            <Step key={2} icon={renderStatusIcon('draft', false)} />,
          ]
        ) : publicationScheduled ? (
          [
            <Step
              key={0}
              sx={styles.step}
              icon={renderStatusIcon('draft', true)}
              nextConnector={<Connector>{publicationScheduledIn}</Connector>}
            />,
            <Step
              key={1}
              sx={styles.step}
              icon={renderStatusIcon('published', false)}
            />,
          ]
        ) : (
          [
            <Step
              key={0}
              sx={styles.step}
              icon={renderStatusIcon('published', true)}
              nextConnector={<Connector>{unpublicationScheduledIn}</Connector>}
            />,
            <Step
              key={1}
              sx={styles.step}
              icon={renderStatusIcon('draft', false)}
            />,
          ]
        )
      ) : published ? (
        <Step sx={styles.step} icon={renderStatusIcon('published', true)} />
      ) : unpublished ? (
        <Step sx={styles.step} icon={renderStatusIcon('draft', true)} />
      ) : (
        <Step sx={styles.step} icon={renderStatusIcon('draft', true)} />
      )}
    </Stepper>
  );
};
